import React, { useContext, useReducer, useRef, useCallback, useEffect, useState } from 'react';
import {
	DishesContext,
	dishesInitialState,
	IFilterValues,
	TCountOfDishes,
	TDishesSortBy,
	TFullDishInfoVisibleForm,
	TTypeOfModalWindow,
} from './dishesContext';
import { dishesReducer } from './dishesReducer';
import { DishesActionTypes } from './dishesActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { IGetPaginatedDishes, TDishesSelectedItem, IDishes } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { getPaginatedDishes, deleteDish } from 'backend/dishes';

const DishesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(dishesReducer, dishesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IDishes>, IGetPaginatedDishes>(
		isMounted.current ? getPaginatedDishes : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			categoryId: state.IDSelectedCategory,
			subCategoryId: state.IDSelectedSubCategory,
			priceFrom: state.priceFrom ? Number(state.priceFrom) : null,
			priceTo: state.priceTo ? Number(state.priceTo) : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByPrice: state.sortBy === 'Price1-10' || state.sortBy === 'Price10-1' ? state.sortBy : null,
			isUnassignedDishes: state.isUnassignedDishes,
			isDisabledDish: state.isDisabledDish,
			isShowOnlyComboItems: state.isShowOnlyComboItems,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: DishesActionTypes.SET_DISHES,
				payload: {
					dishes: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteDish(state.dishIDForDeletion);
			const filteredDishes = state.dishes.dishes.filter((el) => el.id !== state.dishIDForDeletion);
			dispatch({
				type: DishesActionTypes.SET_DISHES,
				payload: {
					dishes: {
						dishesCount: state.dishes.dishesCount - 1,
						dishes: filteredDishes,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: DishesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.dishIDForDeletion, state.dishes]);

	const changeSearchString = useCallback(
		(value: string) => {
			if (allowChangingSearchString) {
				dispatch({
					type: DishesActionTypes.CHANGE_SEARCH_STRING,
					payload: {
						searchString: value,
					},
				});
			}
		},
		[allowChangingSearchString],
	);

	const setIDCustomer = useCallback((value: TDishesSelectedItem) => {
		dispatch({
			type: DishesActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: TDishesSelectedItem) => {
		dispatch({
			type: DishesActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: TDishesSelectedItem) => {
		dispatch({
			type: DishesActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const setIDSubCategory = useCallback((value: TDishesSelectedItem) => {
		dispatch({
			type: DishesActionTypes.SET_ID_SUB_CATEGORY,
			payload: {
				IDSelectedSubCategory: value,
			},
		});
	}, []);

	const setPriceFrom = useCallback((value: string) => {
		dispatch({
			type: DishesActionTypes.SET_PRICE_FROM,
			payload: {
				priceFrom: value,
			},
		});
	}, []);

	const setPriceTo = useCallback((value: string) => {
		dispatch({
			type: DishesActionTypes.SET_PRICE_TO,
			payload: {
				priceTo: value,
			},
		});
	}, []);

	const resetSelectedControls = useCallback(() => dispatch({ type: DishesActionTypes.RESET_SELECTED_CONTROLS }), []);

	const setSortBy = useCallback((value: TDishesSortBy) => {
		dispatch({
			type: DishesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: value,
			},
		});
	}, []);

	const toggleUnassignedDishes = useCallback(() => {
		dispatch({ type: DishesActionTypes.TOGGLE_UNASSIGNED_DISHES });
	}, []);

	const toggleDisabledDishes = useCallback(() => {
		dispatch({ type: DishesActionTypes.TOGGLE_DISABLED_DISHES });
	}, []);

	const toggleShowComboItems = useCallback(() => {
		dispatch({ type: DishesActionTypes.TOGGLE_SHOW_COMBO_ITEMS });
	}, []);

	const setCountOfItems = useCallback((value: TCountOfDishes) => {
		dispatch({
			type: DishesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: value,
			},
		});
	}, []);

	const changeCurrentPage = useCallback((value: number) => {
		dispatch({
			type: DishesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: value,
			},
		});
	}, []);

	const setDishIDForDeletion = useCallback((value: string) => {
		dispatch({
			type: DishesActionTypes.SET_DISH_ID_FOR_DELETION,
			payload: {
				dishIDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	}, []);

	const setDishIDForFullInfo = useCallback((value: string) => {
		dispatch({
			type: DishesActionTypes.SET_DISH_ID_FOR_FULL_INFO,
			payload: {
				dishIDForFullInfo: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: DishesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const changeFullDishInfoVisibleForm = useCallback((value: TFullDishInfoVisibleForm) => {
		dispatch({
			type: DishesActionTypes.CHANGE_FULL_DISH_INFO_VISIBLE_FORM,
			payload: {
				fullDishInfoVisibleForm: value,
			},
		});
	}, []);

	const setFiltersFromQueryString = useCallback(
		(value: IFilterValues) => {
			dispatch({
				type: DishesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
				payload: {
					filters: value,
				},
			});
			isMounted.current = true;
			refetch();
		},
		[refetch, isMounted],
	);

	return (
		<DishesContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				setIDSubCategory,
				setPriceFrom,
				setPriceTo,
				resetSelectedControls,
				setSortBy,
				toggleUnassignedDishes,
				toggleDisabledDishes,
				toggleShowComboItems,
				setCountOfItems,
				changeCurrentPage,
				setDishIDForDeletion,
				setDishIDForFullInfo,
				changeTypeOfModalWindow,
				changeFullDishInfoVisibleForm,
				setFiltersFromQueryString,
				dishes: state.dishes,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				IDSelectedSubCategory: state.IDSelectedSubCategory,
				priceFrom: state.priceFrom,
				priceTo: state.priceTo,
				sortBy: state.sortBy,
				isDisabledDish: state.isDisabledDish,
				isUnassignedDishes: state.isUnassignedDishes,
				isShowOnlyComboItems: state.isShowOnlyComboItems,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				dishIDForDeletion: state.dishIDForDeletion,
				dishIDForFullInfo: state.dishIDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
				fullDishInfoVisibleForm: state.fullDishInfoVisibleForm,
			}}>
			{children}
		</DishesContext.Provider>
	);
};

export default DishesState;
